/**
 * Реестр Phosphor-иконок (inline SVG) для навигации tenant-app.
 *
 * Per дизайн manager v3 § 1.6: «где иконки нормальные, что за квадратики?» —
 * вместо пустых серых <span data-icon> рисуем НАСТОЯЩИЕ SVG (Phosphor-style:
 * 1.5px stroke, rounded caps/joins, color inherit через currentColor).
 *
 * Контуры взяты из канонического мокапа § 1.6 (LovArt design report).
 * Иконки самодостаточны (без внешних зависимостей/спрайтов) — бандл мелкий.
 */
import type { SVGProps } from 'react';

/** Базовые атрибуты Phosphor-style: stroke=currentColor, без заливки. */
const BASE: SVGProps<SVGSVGElement> = {
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.5,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  'aria-hidden': true,
  focusable: false,
};

/** Карта: имя иконки → набор path/элементов. */
const PATHS: Readonly<Record<string, JSX.Element>> = {
  house: <path d="M3 12L12 4l9 8v9a1 1 0 01-1 1h-5v-7H10v7H5a1 1 0 01-1-1z" />,
  'list-checks': (
    <>
      <path d="M4 6h16M4 12h16M4 18h16" />
      <path d="M3 6l1 1 2-2M3 12l1 1 2-2M3 18l1 1 2-2" />
    </>
  ),
  'door-open': (
    <>
      <path d="M14 3v18M14 3l6 4v14H4a1 1 0 01-1-1V4a1 1 0 011-1h10z" />
      <circle cx="16" cy="12" r="0.7" />
    </>
  ),
  'calendar-blank': (
    <>
      <rect x="3" y="5" width="18" height="16" rx="2" />
      <path d="M3 9h18M8 3v4M16 3v4" />
    </>
  ),
  bed: <path d="M3 18v-6a4 4 0 014-4h10a4 4 0 014 4v6M3 18h18M6 18v-3a1 1 0 011-1h10a1 1 0 011 1v3" />,
  gear: (
    <>
      <circle cx="12" cy="12" r="3" />
      <path d="M12 2.5l1.4 2.6 2.9-.6 .6 2.9 2.6 1.4-1.4 2.6 1.4 2.6-2.6 1.4-.6 2.9-2.9-.6L12 21.5l-1.4-2.6-2.9.6-.6-2.9-2.6-1.4 1.4-2.6-1.4-2.6 2.6-1.4 .6-2.9 2.9.6z" />
    </>
  ),
  broom: (
    <>
      <path d="M19 5l-7 7" />
      <path d="M12 12l-3-3" />
      <path d="M9 9l-5 9 9-5a4 4 0 00-4-4z" />
    </>
  ),
  'fork-knife': <path d="M6 3v18M6 12h2a3 3 0 003-3V3M18 3v8a3 3 0 01-3 3M18 14v7" />,
  package: (
    <>
      <rect x="3" y="7" width="18" height="13" rx="1" />
      <path d="M3 11h18M8 3v4M16 3v4" />
    </>
  ),
  'users-three': (
    <>
      <circle cx="9" cy="8" r="3" />
      <circle cx="17" cy="9" r="2" />
      <path d="M3 20a6 6 0 0112 0M14 20a4.5 4.5 0 017 0" />
    </>
  ),
  sparkle: (
    <>
      <path d="M12 3l1.8 4.2L18 9l-4.2 1.8L12 15l-1.8-4.2L6 9l4.2-1.8z" />
      <path d="M19 16l.8 1.8L21.6 19l-1.8.8L19 22l-.8-1.8L16.4 19l1.8-.8z" />
    </>
  ),
  'chart-line': <path d="M3 3v18h18M7 15l4-4 3 3 5-6" />,
  timer: (
    <>
      <circle cx="12" cy="13" r="8" />
      <path d="M12 9v4l3 2M10 3h4M12 3v2" />
    </>
  ),
  star: <path d="M12 3l2.7 5.7 6.3.9-4.6 4.4 1.1 6.2L12 17.3 6.5 20.2l1.1-6.2L3 9.6l6.3-.9z" />,
  truck: (
    <path d="M3 8h11v8H3zM14 11h4l3 3v2h-7zM7 19a2 2 0 100-4 2 2 0 000 4zM17 19a2 2 0 100-4 2 2 0 000 4z" />
  ),
  'calendar-check': (
    <>
      <rect x="3" y="5" width="18" height="16" rx="2" />
      <path d="M3 9h18M8 3v4M16 3v4M9 14l2 2 4-4" />
    </>
  ),
  'credit-card': (
    <>
      <rect x="2" y="6" width="20" height="13" rx="2" />
      <path d="M2 10h20M6 15h4" />
    </>
  ),
  user: (
    <>
      <circle cx="12" cy="9" r="4" />
      <path d="M4 21a8 8 0 0116 0" />
    </>
  ),
  // Стандартный «выход»: дверной проём + стрелка наружу (#81).
  'sign-out': (
    <>
      <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4" />
      <path d="M16 17l5-5-5-5M21 12H9" />
    </>
  ),
  list: <path d="M4 6h16M4 12h16M4 18h16" />,
  circle: <circle cx="12" cy="12" r="9" />,
};

export interface NavIconProps {
  /** Имя Phosphor-иконки. Неизвестное → нейтральный кружок (не квадрат). */
  name: string;
  /** Размер в px (квадрат). По умолчанию 18. */
  size?: number;
  /** Доп. класс (для desktop sidebar / mobile tab размеров). */
  className?: string;
}

/**
 * Рендерит inline SVG Phosphor-иконку по имени. Цвет наследуется
 * (currentColor) — активный пункт перекрашивает иконку вместе с лейблом.
 */
export function NavIcon({ name, size = 18, className }: NavIconProps): JSX.Element {
  const path = PATHS[name] ?? PATHS.circle;
  return (
    <svg
      {...BASE}
      width={size}
      height={size}
      {...(className ? { className } : {})}
      data-icon-name={name}
    >
      {path}
    </svg>
  );
}

/** Имена всех зарегистрированных иконок (для тестов/проверок). */
export const REGISTERED_ICONS = Object.keys(PATHS);
